// ============================================
// Pipeline Business Logic — Pure Functions
// Extracted from PipelineView.tsx and usePipelineData.ts
// No React, no Firebase, no side effects.
// ============================================

import type {
  DocStatus, DocDocument, DocTemplate,
  GCConflict, ConflictSeverity, LibraryScore,
  BrandConfig,
} from '../types/docops';

// ── Status Constants ──

/** Statuses that count as "reviewed" (attorney has seen it or beyond) */
export const REVIEWED_STATUSES: ReadonlySet<DocStatus> = new Set([
  'attorney_review', 'attorney_approved', 'final', 'signed',
]);

/** Statuses that count as "completed" (blocker is resolved) */
export const COMPLETED_STATUSES: ReadonlySet<DocStatus> = new Set([
  'attorney_approved', 'final', 'signed',
]);

/** Statuses that mean "not yet generated" */
export const UNGENERATED_STATUSES: ReadonlySet<DocStatus> = new Set([
  'not_started', 'generating',
]);

/** Per-conflict severity score penalties */
export const CONFLICT_PENALTY: Record<ConflictSeverity, number> = {
  critical: 15,
  high: 10,
  medium: 3,
  low: 1,
};

// ── Conflict Scoring ──

/** Sum conflict severity penalties for a set of conflicts. */
export function calculateConflictPenalty(conflicts: GCConflict[]): number {
  return conflicts.reduce(
    (penalty, c) => penalty + (CONFLICT_PENALTY[c.severity] ?? 0),
    0,
  );
}

/**
 * Compute effective audit score after conflict penalties.
 * Returns undefined if the document has no auditScore.
 */
export function calculateEffectiveScore(
  auditScore: number | undefined,
  docConflicts: GCConflict[],
): number | undefined {
  if (auditScore == null) return undefined;
  const penalty = calculateConflictPenalty(docConflicts);
  return Math.max(0, auditScore - penalty);
}

/**
 * Get all conflicts involving a specific template.
 * Pass excludePending: true to filter out 'pending' status conflicts.
 */
export function getDocConflicts(
  allConflicts: GCConflict[],
  templateId: string,
  options?: { excludePending?: boolean },
): GCConflict[] {
  return allConflicts.filter(c => {
    if (options?.excludePending && c.status === 'pending') return false;
    return c.documentA === templateId || c.documentB === templateId;
  });
}

// ── Attorney Review Promotion ──

export interface AttorneyReadinessResult {
  attyReady: DocDocument[];
  conflictHeld: DocDocument[];
  depHeld: DocDocument[];
  lowScore: DocDocument[];
}

/**
 * Categorize "Ready for Review" docs into promotion buckets.
 * - attyReady: effective score >= 95, no critical/high conflicts, deps met
 * - conflictHeld: score >= 95 but has critical/high conflicts
 * - depHeld: score >= 95 but waiting on dependencies
 * - lowScore: effective score < 95
 */
export function categorizeAttorneyReadiness(
  stageDocs: DocDocument[],
  allConflicts: GCConflict[],
  templates: DocTemplate[],
  allDocuments: DocDocument[],
): AttorneyReadinessResult {
  const realConflicts = allConflicts.filter(c => c.status !== 'pending');

  const getEffective = (d: DocDocument): number => {
    const dConflicts = getDocConflicts(realConflicts, d.templateId);
    return calculateEffectiveScore(d.auditScore, dConflicts) ?? 0;
  };

  const attyReady = stageDocs.filter(d => {
    if (getEffective(d) < 95) return false;
    const tpl = templates.find(t => t.id === d.templateId);
    if (tpl?.dependencies?.length) {
      const depsReady = tpl.dependencies.every(depId => {
        const depDoc = allDocuments.find(x => x.templateId === depId);
        return depDoc && !UNGENERATED_STATUSES.has(depDoc.status);
      });
      if (!depsReady) return false;
    }
    const hasSeriousConflict = realConflicts.some(
      c => (c.severity === 'critical' || c.severity === 'high') &&
           (c.documentA === d.templateId || c.documentB === d.templateId),
    );
    return !hasSeriousConflict;
  });

  const conflictHeld = stageDocs.filter(d => {
    if (getEffective(d) < 95) return false;
    if (attyReady.includes(d)) return false;
    return realConflicts.some(
      c => (c.severity === 'critical' || c.severity === 'high') &&
           (c.documentA === d.templateId || c.documentB === d.templateId),
    );
  });

  const lowScore = stageDocs.filter(d => getEffective(d) < 95);

  const depHeld = stageDocs.filter(d => {
    if (getEffective(d) < 95) return false;
    if (attyReady.includes(d) || conflictHeld.includes(d)) return false;
    return true;
  });

  return { attyReady, conflictHeld, depHeld, lowScore };
}

// ── Round Locking ──

export interface RoundLockResult {
  locked: boolean;
  reason: string;
}

/**
 * Determine if a generation round is locked.
 * Locked when earlier rounds have docs not yet in attorney_review+.
 * Round 1 is always unlocked.
 */
export function isRoundLocked(
  roundNum: number,
  allDocuments: DocDocument[],
  templates: DocTemplate[],
): RoundLockResult {
  if (roundNum <= 1) return { locked: false, reason: '' };
  const prevDocs = allDocuments.filter(d => {
    const r = templates.find(t => t.id === d.templateId)?.generationRound ?? 99;
    return r < roundNum && r < 99;
  });
  const notReviewed = prevDocs.filter(d => !REVIEWED_STATUSES.has(d.status));
  if (notReviewed.length === 0) return { locked: false, reason: '' };
  const blockedRound = Math.min(
    ...notReviewed.map(d => templates.find(t => t.id === d.templateId)?.generationRound ?? 99),
  );
  const count = notReviewed.filter(
    d => (templates.find(t => t.id === d.templateId)?.generationRound ?? 99) === blockedRound,
  ).length;
  return { locked: true, reason: `Round ${blockedRound}: ${count} doc(s) awaiting attorney review` };
}

// ── Document Helpers ──

/**
 * Days since the document was last edited or generated.
 * Accepts optional `now` for deterministic testing.
 */
export function getDaysInState(doc: DocDocument, now?: number): number {
  if (!doc.lastEditedAt && !doc.generatedAt) return 0;
  const dateStr = doc.lastEditedAt || doc.generatedAt;
  if (!dateStr) return 0;
  return Math.floor(((now ?? Date.now()) - new Date(dateStr).getTime()) / 86400000);
}

export interface FilterCriteria {
  searchQuery: string;
  activeFilters: Set<string>;
  allConflicts: GCConflict[];
  now?: number;
}

/** Check if a document passes the current search + filter criteria. */
export function filterDocument(doc: DocDocument, criteria: FilterCriteria): boolean {
  if (criteria.searchQuery) {
    const q = criteria.searchQuery.toLowerCase();
    if (!doc.title.toLowerCase().includes(q)) return false;
  }
  if (criteria.activeFilters.has('blocker') && !doc.launchBlocker) return false;
  if (criteria.activeFilters.has('attorney') && !doc.attorneyReviewRequired) return false;
  if (criteria.activeFilters.has('conflicts')) {
    const hasConflict = criteria.allConflicts.some(
      c => c.documentA === doc.templateId || c.documentB === doc.templateId,
    );
    if (!hasConflict) return false;
  }
  if (criteria.activeFilters.has('overdue')) {
    if (getDaysInState(doc, criteria.now) < 14) return false;
  }
  return true;
}

// ── Readiness Score ──

export interface ReadinessScoreResult {
  readinessScore: number;
  qualityScore: number;
  genPercent: number;
  blockerPercent: number;
}

/**
 * Readiness = Quality (40%) + Generated% (30%) + Blocker% (30%).
 * Returns all zeros if no documents are scored yet.
 */
export function calculateReadinessScore(
  libraryScore: LibraryScore | null,
  totalDocs: number,
  generatedCount: number,
  documents: DocDocument[],
): ReadinessScoreResult {
  const qualityScore = libraryScore?.combinedScore ?? 0;
  const genPercent = totalDocs > 0 ? Math.round((generatedCount / totalDocs) * 100) : 0;
  const blockerTotal = documents.filter(d => d.launchBlocker).length;
  const blockerComplete = documents.filter(
    d => d.launchBlocker && COMPLETED_STATUSES.has(d.status),
  ).length;
  const blockerPercent = blockerTotal > 0
    ? Math.round((blockerComplete / blockerTotal) * 100)
    : 100;
  const readinessScore = libraryScore && libraryScore.generatedDocuments > 0
    ? Math.round((qualityScore * 0.4) + (genPercent * 0.3) + (blockerPercent * 0.3))
    : 0;
  return { readinessScore, qualityScore, genPercent, blockerPercent };
}

// ── Dependency Info ──

export interface DepInfo {
  blocks: number;
  blockedBy: number;
  blocksNames: string[];
  blockedByNames: string[];
}

/** Get dependency info for a document: what it blocks and what blocks it. */
export function getDepInfo(doc: DocDocument, templates: DocTemplate[]): DepInfo {
  const tpl = templates.find(t => t.id === doc.templateId);
  const blockedByIds = tpl?.dependencies ?? [];
  const blockedByNames = blockedByIds.map(
    id => templates.find(x => x.id === id)?.title ?? id,
  );
  const blocksTemplates = templates.filter(
    t => t.dependencies?.includes(doc.templateId),
  );
  const blocksNames = blocksTemplates.map(t => t.title);
  return {
    blocks: blocksTemplates.length,
    blockedBy: blockedByIds.length,
    blocksNames,
    blockedByNames,
  };
}

/**
 * Compute priority score for dependency-based sorting of needs_generation docs.
 * Higher score = should generate earlier.
 */
export function getDepImportance(
  doc: DocDocument,
  templates: DocTemplate[],
  conflicts: GCConflict[],
): number {
  let score = 0;
  const blocksCount = templates.filter(
    t => t.dependencies?.includes(doc.templateId),
  ).length;
  score += blocksCount * 10;
  if (doc.launchBlocker) score += 50;
  const criticalPairs = conflicts.filter(
    c => (c.severity === 'critical' || c.severity === 'high') &&
         (c.documentA === doc.templateId || c.documentB === doc.templateId),
  );
  score += criticalPairs.length * 20;
  const tpl = templates.find(t => t.id === doc.templateId);
  if (tpl?.generationRound) score += (7 - tpl.generationRound) * 5;
  return score;
}

// ── Pre-Flight Config Check ──

export interface ConfigGap {
  field: keyof BrandConfig;
  label: string;
  templates: string[];
}

/**
 * Scan templates' requiredVariables against brand config.
 * Returns fields that are blank/empty and which templates need them.
 */
export function getConfigGaps(
  tpls: DocTemplate[],
  config: BrandConfig,
  fieldLabels: Record<string, string>,
): ConfigGap[] {
  const gapMap = new Map<string, { label: string; templates: string[] }>();

  for (const tpl of tpls) {
    for (const varName of tpl.requiredVariables) {
      const val = (config as unknown as Record<string, unknown>)[varName];
      const isEmpty = val === undefined || val === null || val === ''
        || (typeof val === 'number' && val === 0)
        || (Array.isArray(val) && val.length === 0);
      if (!isEmpty) continue;

      const existing = gapMap.get(varName);
      if (existing) {
        existing.templates.push(tpl.title);
      } else {
        gapMap.set(varName, {
          label: fieldLabels[varName] ?? varName,
          templates: [tpl.title],
        });
      }
    }
  }

  return Array.from(gapMap.entries()).map(([field, data]) => ({
    field: field as keyof BrandConfig,
    label: data.label,
    templates: data.templates,
  }));
}
